import pMemoize from "@-/com.npmjs.p-memoize";
import { boundedPromise } from "@-/dev.improve.haniverse.lib.deadline";
import type { BackupStore } from "#backup-types";

/**
 * Opening a local IndexedDB database takes milliseconds. It is bounded because WebKit has shipped
 * bugs where `indexedDB.open` fires NO event at all (neither success nor error), and this open is
 * memoized: a request that never answered used to pin every backup read and write in the page
 * behind one promise that could never settle. A timed-out open is not cached (`pMemoize` drops
 * rejections), so the next backup operation tries again.
 */
const OPEN_BACKUP_STORE_DEADLINE_MS = 10_000;

/**
 * One transaction on the backup key-value store: at most one archive chunk read or written. A
 * transaction still open after this long is stuck; it is aborted, so a timed-out write can never
 * land after its caller was told it failed.
 */
const BACKUP_STORE_TRANSACTION_DEADLINE_MS = 30_000;

const openBackupStore = pMemoize(async () =>
  boundedPromise<IDBDatabase>(
    { deadline: OPEN_BACKUP_STORE_DEADLINE_MS, operation: "open the local backup store" },
    (resolve, reject, signal) => {
      const request = indexedDB.open("haniverse-local-backups-v1", 1);
      request.onupgradeneeded = () => request.result.createObjectStore("entries");
      request.addEventListener("error", () => reject(request.error));
      request.onsuccess = () => {
        if (signal.aborted) {
          // Answered after we gave up: nobody will use this connection, so do not leak it.
          request.result.close();
          return;
        }
        resolve(request.result);
      };
      request.onblocked = () => reject(new Error("Local backup storage is blocked by another tab"));
    },
  ),
);

async function transaction<T>(
  mode: IDBTransactionMode,
  action: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
  const db = await openBackupStore();
  return boundedPromise<T>(
    { deadline: BACKUP_STORE_TRANSACTION_DEADLINE_MS, operation: `local backup store ${mode}` },
    (resolve, reject, signal) => {
      const tx = db.transaction("entries", mode, { durability: "strict" });
      const request = action(tx.objectStore("entries"));
      tx.oncomplete = () => resolve(request.result);
      tx.addEventListener("error", () => reject(tx.error));
      tx.addEventListener("abort", () =>
        reject(tx.error ?? new Error("Backup storage transaction was interrupted")),
      );
      signal.addEventListener(
        "abort",
        () => {
          try {
            tx.abort();
          } catch {
            // Already finished: nothing left to stop.
          }
        },
        { once: true },
      );
    },
  );
}

export const backupStore: BackupStore = {
  get: async (key) => {
    const value: unknown = await transaction("readonly", (store) => store.get(key));
    if (value === undefined) {
      return null;
    }
    if (typeof value !== "string") {
      throw new TypeError("Invalid backup storage entry");
    }
    return value;
  },
  keys: async (prefix) => {
    const keys = await transaction("readonly", (store) =>
      store.getAllKeys(IDBKeyRange.bound(prefix, prefix + "￿", false, true)),
    );
    return keys.filter((key): key is string => typeof key === "string");
  },
  remove: async (key) => {
    await transaction("readwrite", (store) => store.delete(key));
  },
  set: async (key, value) => {
    await transaction("readwrite", (store) => store.put(value, key));
  },
};
